import { Core } from "@faire/design-tokens";
import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { useSSOProviderOptionsStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOProviderOptionsStore";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { Avatar } from "@faire/slate/core/avatar";
import { Button } from "@faire/web--source/slate/Button";
import { Apple } from "@faire/web--source/slate/icons/external/Apple";
import { Row, Column } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { SocialSignOnProvider } from "@faire/web-api--source/indigofair/data/SocialSignOnProvider";
import React, { type JSX } from "react";
import styled from "styled-components";

export const SSOAvatar: React.FC = () => {
  const { selectedProvider, signUpSuggestions, setPromptSSO } = useSSOStore();
  const { resetProviders } = useSSOProviderOptionsStore();

  const { first_name, last_name, email_address } = signUpSuggestions;

  const renderAvatar = () => {
    switch (selectedProvider) {
      case SocialSignOnProvider.APPLE:
        return (
          <IconContainer align="center" justify="center">
            <Apple fontSize="20px" />
          </IconContainer>
        );
      case SocialSignOnProvider.GOOGLE:
        return (
          <Avatar
            size="xSmall"
            image={IImage.build({
              width: 40,
              height: 40,
              url: signUpSuggestions.profile_image_url,
            })}
          />
        );
      default:
        return (
          <Avatar
            size="xSmall"
            image={IImage.build({
              width: 40,
              height: 40,
              url: EAEAEA_PIXEL,
            })}
          />
        );
    }
  };

  return (
    <Wrapper data-test-id="ssoAvatar">
      <Row align="center">
        {renderAvatar()}
        <Spacer width="2x" />

        <OverflowColumn>
          {first_name || last_name ? (
            <OverflowEllipsis>
              {(first_name ?? "") +
                (first_name && last_name ? " " : "") +
                (last_name ?? "")}
            </OverflowEllipsis>
          ) : null}
          <OverflowEllipsis color={Core.text.subdued}>
            {email_address}
          </OverflowEllipsis>
        </OverflowColumn>
      </Row>
      <Column align={{ mobileAndAbove: "center", tabletAndAbove: "start" }}>
        <Spacer height={12} />
        <Row>
          <Spacer width={{ mobileAndAbove: 0, tabletAndAbove: "7x" }} />
          <Typography align="start">
            <StrictLocalMsg
              defaultMessage="Not you? <LinkFormatting>Choose a different account</LinkFormatting>"
              values={{
                LinkFormatting: LinkFormatting(() => {
                  resetProviders();
                  setPromptSSO(false);
                }),
              }}
            />
          </Typography>
        </Row>
      </Column>
    </Wrapper>
  );
};

const LinkFormatting = (onClick: () => void) =>
  function (chunks: JSX.Element) {
    return (
      <Button variant="tertiary" linkTo="/?signUp=1" onClick={onClick}>
        {chunks}
      </Button>
    );
  };

const Wrapper = styled(Column)`
  border: 1px solid ${Core.border.muted};
  padding: ${getSpacing("3x", "2x")};
`;

const IconContainer = styled(Row)`
  background: ${Core.surface.inverse};
  border: 1px solid ${Core.border.muted};
  flex-shrink: 0;
  border-radius: 50%;
  width: 40px;
  height: 40px;
`;

const OverflowEllipsis = styled(Typography)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const OverflowColumn = styled(Column)`
  overflow: hidden;
`;
